import type { BoardObject } from '../core/interfaces/board-object.interface.js';

/**
 * Canonical event name for the debounced board-changed CustomEvent dispatched
 * by `<o2d-whiteboard>`. Prefixed (review-finding "consensus-MAJOR") to avoid
 * collisions with the very generic `'changed'` identifier used by form-input
 * libraries and to align with the sibling `'whiteboard-command'` event name.
 */
export const WHITEBOARD_CHANGED_EVENT = 'whiteboard-changed' as const;

/**
 * Detail payload of the `whiteboard-changed` CustomEvent dispatched by `<o2d-whiteboard>`.
 *
 * Emitted at most once per 300ms debounce window after any mutation observed
 * on the internal bus `domain$` stream.
 *
 * - `objects`: current board snapshot at emit time. The array itself is fresh,
 *   but contained `BoardObject` instances are LIVE references aliased with the
 *   whiteboard's internal state. Treat as read-only — mutating an entry (e.g.
 *   `detail.objects[0].x = 999`) corrupts board state silently, bypasses bus
 *   events, history push, and renderer notify. Clone before mutation.
 * - `isDirty`: `true` iff the content signature of `objects` differs from the
 *   signature captured by the last `config` assignment or `markSaved()` call.
 */
export interface WhiteboardChangedEventDetail {
  readonly objects: readonly BoardObject[];
  readonly isDirty: boolean;
}

/**
 * Type alias for the `whiteboard-changed` CustomEvent dispatched by
 * `<o2d-whiteboard>`. Consumers can use this to declare a typed listener
 * variable when needed.
 */
export type WhiteboardChangedEvent = CustomEvent<WhiteboardChangedEventDetail>;

// Global augmentation (review-finding M3): addEventListener('whiteboard-changed', ...)
// becomes type-safe for any HTMLElement consumer that imports from
// '@o2d/whiteboard'. Compile-time only — no runtime effect.
declare global {
  interface HTMLElementEventMap {
    'whiteboard-changed': WhiteboardChangedEvent;
  }
}
